import {
  TABLE_COL_MIN_WIDTH,
  TABLE_ROW_MIN_HEIGHT,
} from "@library-elements/table";
import type { ITableSettings } from "@library-elements/table";
import {
  type MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { calculateAutoFitWidth } from "../utils/table-auto-fit";
import { measureInCellElementFloors } from "../utils/measure-in-cell-element-floors";
import { useTableUpdateColWidths } from "./use-table-update-col-widths";
import { useTableUpdateRowHeights } from "./use-table-update-row-heights";

interface IProps {
  colsMinWidth: MutableRefObject<number[]>;
  elementSettings: ITableSettings;
  tableWrapperRef: MutableRefObject<HTMLDivElement | null>;
}

interface IResizeData {
  type: "row" | "col";
  cellEl: HTMLElement;
  originalWidth: number;
  pressPoint: { x: number; y: number };
  // When a multi-cell selection is active and contains the dragged cell,
  // these list the cols/rows that should resize together. The anchor col/row
  // (dragged) becomes the target size; all other entries match it.
  selectedCols?: number[];
  selectedRows?: number[];
}

export const useTableCellResize = ({
  tableWrapperRef,
  colsMinWidth,
  elementSettings,
}: IProps) => {
  const [resizeData, setResizeData] = useState<IResizeData | null>(null);
  const indicatorElRef = useRef<HTMLDivElement | null>(null);

  // Mirrors `resizeData` as a ref so other document-level mousemove handlers
  // can cheaply opt out for the duration of a drag without re-subscribing or
  // re-rendering. Kept in sync from two places on purpose: `beginResize` sets
  // it synchronously on mousedown (a state update would not land until after
  // the commit, leaving a gap a mousemove could slip through) and the drag
  // effect below owns clearing it, which also covers the internal
  // `setResizeData(null)` on mouseup and an unmount mid-drag.
  const isResizingRef = useRef(false);

  const originalWidth = useRef<number | null>(null);
  const originalRowHeight = useRef<number | null>(null);
  const originalNextWidth = useRef<number | null>(null);
  // Captured once at drag start for multi-col: widths of every selected col
  // and of the borrow col (the col right after the rightmost selected one).
  const originalSelectedWidthsRef = useRef<number[] | null>(null);
  const originalBorrowWidthRef = useRef<number | null>(null);
  // Captured once at drag start for multi-row: original heights of each
  // selected row, indexed by row number.
  const originalSelectedRowHeightsRef = useRef<Map<number, number> | null>(null);

  // Pending widths/heights accumulated during drag; committed to Redux on mouseup.
  const pendingWidthsRef = useRef<number[] | null>(null);
  const pendingHeightsRef = useRef<number[] | null>(null);

  // Per-col floors from in-cell element widths, captured once at drag start:
  // a column can't be dragged below its widest element, so the drag never
  // shrinks an element or pushes it over neighbouring cells.
  const elementFloorsRef = useRef<number[]>([]);
  const minForCol = useCallback(
    (idx: number) =>
      Math.max(
        colsMinWidth.current[idx] ?? TABLE_COL_MIN_WIDTH,
        elementFloorsRef.current[idx] ?? 0,
      ),
    [colsMinWidth],
  );

  const { updateTableColWidths } = useTableUpdateColWidths();
  const { updateTableRowHeights } = useTableUpdateRowHeights();

  const handleMouseResizeRef = useRef<(e: MouseEvent) => void>(() => {});

  const updateIndicator = useCallback(
    (value: number, clientX: number, clientY: number) => {
      const el = indicatorElRef.current;
      if (!el) return;
      el.style.display = "flex";
      el.style.left = `${clientX + 14}px`;
      el.style.top = `${clientY + 14}px`;
      el.textContent = `${value}px`;
    },
    [],
  );

  useEffect(() => {
    if (!resizeData) return;
    isResizingRef.current = true;

    const reset = () => {
      originalWidth.current = null;
      originalNextWidth.current = null;
      originalRowHeight.current = null;
      originalSelectedWidthsRef.current = null;
      originalBorrowWidthRef.current = null;
      originalSelectedRowHeightsRef.current = null;
    };

    let rafId: number | null = null;
    let latestEvent: MouseEvent | null = null;

    const onMouseMove = (e: MouseEvent) => {
      latestEvent = e;
      if (rafId != null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        if (latestEvent) handleMouseResizeRef.current(latestEvent);
      });
    };

    const handleMouseUp = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (rafId != null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      if (pendingWidthsRef.current) {
        updateTableColWidths(pendingWidthsRef.current);
        pendingWidthsRef.current = null;
      }
      if (pendingHeightsRef.current) {
        updateTableRowHeights(pendingHeightsRef.current);
        pendingHeightsRef.current = null;
      }
      setResizeData(null);
      if (indicatorElRef.current) {
        indicatorElRef.current.style.display = "none";
      }
      reset();
    };

    reset();

    elementFloorsRef.current =
      resizeData.type === "col" && tableWrapperRef.current
        ? measureInCellElementFloors(tableWrapperRef.current)
        : [];

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      isResizingRef.current = false;
      if (rafId != null) cancelAnimationFrame(rafId);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [resizeData, updateTableColWidths, updateTableRowHeights, tableWrapperRef]);

  const beginResize = useCallback((data: IResizeData | null) => {
    isResizingRef.current = !!data;
    setResizeData(data);
  }, []);

  const handleMouseResize = useCallback(
    (e: MouseEvent) => {
      if (!resizeData) return;
      e.preventDefault();
      e.stopPropagation();

      const wrapperEl = tableWrapperRef.current;
      if (!wrapperEl) return;
      const wrapperRect = wrapperEl.getBoundingClientRect();

      const { cellEl, type, pressPoint } = resizeData;

      const cellRect = cellEl.getBoundingClientRect();

      if (!originalWidth.current) {
        originalWidth.current = cellRect.width;
      }

      if (type === "row") {
        const row = Number(cellEl.getAttribute("data-cell-row"));
        if (Number.isNaN(row)) return;

        const rowEl = cellEl.parentElement;
        if (!(rowEl instanceof HTMLTableRowElement)) return;

        if (!originalRowHeight.current) {
          originalRowHeight.current = rowEl.getBoundingClientRect().height;
        }

        const diff = e.y - pressPoint.y;
        const height = Math.max(
          TABLE_ROW_MIN_HEIGHT,
          originalRowHeight.current + diff,
        );

        const selectedRows = resizeData.selectedRows;
        const useMulti = selectedRows && selectedRows.length > 1 && selectedRows.includes(row);

        if (useMulti && selectedRows) {
          // Capture original heights for all selected rows once
          if (!originalSelectedRowHeightsRef.current) {
            const map = new Map<number, number>();
            const tbody = rowEl.parentElement;
            if (tbody) {
              const rowEls = tbody.querySelectorAll<HTMLTableRowElement>("tr");
              for (const r of selectedRows) {
                const el = rowEls[r];
                if (el) map.set(r, el.getBoundingClientRect().height);
              }
            }
            originalSelectedRowHeightsRef.current = map;
          }

          const tbody = rowEl.parentElement;
          const rowEls = tbody
            ? tbody.querySelectorAll<HTMLTableRowElement>("tr")
            : null;
          const pending = pendingHeightsRef.current ?? [];
          for (const r of selectedRows) {
            const el = rowEls?.[r];
            if (!el) continue;
            el.style.setProperty("height", `${height}px`);
            pending[r] = height;
          }
          pendingHeightsRef.current = pending;
        } else {
          rowEl.style.setProperty("height", `${height}px`);
          const pending = pendingHeightsRef.current ?? [];
          pending[row] = height;
          pendingHeightsRef.current = pending;
        }
        updateIndicator(Math.round(height), e.clientX, e.clientY);
        return;
      }

      const col = Number(cellEl.getAttribute("data-cell-col"));
      if (Number.isNaN(col)) return;

      const colGroup = wrapperEl.querySelector("colgroup");
      if (!(colGroup instanceof HTMLTableColElement)) return;

      const colEl = colGroup.children[col] as HTMLElement;
      const selectedCols = resizeData.selectedCols;
      const useMulti =
        selectedCols && selectedCols.length > 1 && selectedCols.includes(col);

      // Candidate target width from the mouse position (clamped to this col's min)
      let width = Math.max(minForCol(col), e.x - cellRect.x);
      const newWidths: number[] = [];

      if (useMulti && selectedCols) {
        // Every selected col becomes the same width as the dragged one; the
        // total delta is absorbed by the col right after the rightmost
        // selected col (the "borrow col"). If no borrow col exists (the
        // selection extends to the last col), fall back to single-col.
        const sortedSelected = [...selectedCols].sort((a, b) => a - b);
        const lastSelected = sortedSelected[sortedSelected.length - 1];
        const borrowIdx = lastSelected + 1;
        const borrowEl = colGroup.children[borrowIdx] as HTMLElement | undefined;

        if (borrowEl) {
          if (!originalSelectedWidthsRef.current) {
            originalSelectedWidthsRef.current = sortedSelected.map((c) => {
              const el = colGroup.children[c] as HTMLElement | undefined;
              return el ? el.getBoundingClientRect().width : 0;
            });
          }
          if (originalBorrowWidthRef.current == null) {
            originalBorrowWidthRef.current = borrowEl.getBoundingClientRect().width;
          }

          const origSelected = originalSelectedWidthsRef.current;
          const origBorrow = originalBorrowWidthRef.current;
          const origSelectedSum = origSelected.reduce((a, b) => a + b, 0);
          const borrowMin = minForCol(borrowIdx);
          const n = sortedSelected.length;

          // Cap target so the borrow col doesn't shrink below its min
          const maxTotal = origSelectedSum + (origBorrow - borrowMin);
          const cappedTotal = Math.min(n * width, maxTotal);
          // All selected cols get the same width, so the floor is the
          // largest min among them.
          const finalWidth = Math.max(
            ...sortedSelected.map(minForCol),
            cappedTotal / n,
          );
          width = finalWidth;

          const totalDelta = n * finalWidth - origSelectedSum;
          const borrowNewWidth = Math.max(borrowMin, origBorrow - totalDelta);

          for (const c of sortedSelected) {
            const el = colGroup.children[c] as HTMLElement | undefined;
            if (!el) continue;
            newWidths[c] = (finalWidth / wrapperRect.width) * 100;
            el.style.setProperty("width", `${newWidths[c]}%`);
          }
          newWidths[borrowIdx] = (borrowNewWidth / wrapperRect.width) * 100;
          borrowEl.style.setProperty("width", `${newWidths[borrowIdx]}%`);
        } else {
          // No col to the right of the selection — fall back to single-col
          // drag on the anchor column.
          const nextColEl = colGroup.children[col + 1] as HTMLElement;
          if (nextColEl) {
            const nextCellRect = nextColEl.getBoundingClientRect();
            if (!originalNextWidth.current) {
              originalNextWidth.current = nextCellRect.width;
            }
            const minWidth = minForCol(col + 1);
            width = Math.min(
              originalNextWidth.current + originalWidth.current - minWidth,
              width,
            );
            const nextWidth = Math.max(
              minWidth,
              originalNextWidth.current - (width - originalWidth.current),
            );
            newWidths[col + 1] = (nextWidth / wrapperRect.width) * 100;
            nextColEl.style.setProperty("width", `${newWidths[col + 1]}%`);
          }
          newWidths[col] = (width / wrapperRect.width) * 100;
          colEl.style.setProperty("width", `${newWidths[col]}%`);
        }
      } else {
        const nextColEl = colGroup.children[col + 1] as HTMLElement;
        if (nextColEl) {
          const nextCellRect = nextColEl.getBoundingClientRect();

          if (!originalNextWidth.current) {
            originalNextWidth.current = nextCellRect.width;
          }

          const minWidth = minForCol(col + 1);
          width = Math.min(
            originalNextWidth.current + originalWidth.current - minWidth,
            width,
          );

          const nextWidth = Math.max(
            minWidth,
            originalNextWidth.current - (width - originalWidth.current),
          );

          newWidths[col + 1] = (nextWidth / wrapperRect.width) * 100;
          nextColEl.style.setProperty("width", `${newWidths[col + 1]}%`);
        }

        newWidths[col] = (width / wrapperRect.width) * 100;
        colEl.style.setProperty("width", `${newWidths[col]}%`);
      }

      const pending = pendingWidthsRef.current ?? [];
      for (const idx in newWidths) {
        if (newWidths[idx] != null) pending[idx] = newWidths[idx];
      }
      pendingWidthsRef.current = pending;
      updateIndicator(Math.round(width), e.clientX, e.clientY);
    },
    [resizeData, tableWrapperRef, colsMinWidth, minForCol],
  );

  handleMouseResizeRef.current = handleMouseResize;

  // Persist cursor on body during drag
  useEffect(() => {
    if (!resizeData) return;
    const cursor = resizeData.type === "col" ? "col-resize" : "row-resize";
    document.body.style.cursor = cursor;
    return () => {
      document.body.style.cursor = "";
    };
  }, [resizeData]);

  // Double-click on col resize handle triggers auto-fit
  const handleDoubleClickAutoFit = useCallback(
    (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const colResizeEl = target.closest("[data-is-col-resize]");
      if (!colResizeEl) return;

      const cellEl = colResizeEl.closest("[data-cell-col]");
      if (!(cellEl instanceof HTMLElement)) return;

      const col = Number(cellEl.getAttribute("data-cell-col"));
      if (Number.isNaN(col)) return;

      const wrapperEl = tableWrapperRef.current;
      if (!wrapperEl) return;

      const { tableCols, tableColWidths } = elementSettings;
      const currentWidths = tableColWidths ?? Array.from({ length: tableCols }, () => 100 / tableCols);
      const newWidths = calculateAutoFitWidth(
        col,
        tableCols,
        wrapperEl,
        currentWidths,
        measureInCellElementFloors(wrapperEl),
      );
      updateTableColWidths(newWidths);
    },
    [tableWrapperRef, elementSettings, updateTableColWidths],
  );

  return {
    setResizeData: beginResize,
    handleMouseResize,
    handleDoubleClickAutoFit,
    indicatorElRef,
    isResizingRef,
  };
};
